<template>
  <div class="dish-selector">
    <section class="dish-panel" :aria-label="availableTitle">
      <div class="panel-heading">
        <h3>{{ availableTitle }}</h3>
        <span class="count">{{ dishes.length }}</span>
      </div>
      <p class="panel-hint">Disponibles de la A a la Z. Marca tu selección.</p>
      <label :for="searchId" class="visually-hidden">{{ searchPlaceholder }}</label>
      <el-input
        :id="searchId"
        v-model="search"
        :placeholder="searchPlaceholder"
        :aria-label="searchPlaceholder"
        prefix-icon="el-icon-search"
        clearable
      />
      <div class="search-summary" role="status">
        <span>{{ availableDishes.length }} {{ availableDishes.length === 1 ? itemSingular : itemPlural }}</span>
        <el-button type="text" :disabled="!availableDishes.length" @click="selectVisible">
          Seleccionar resultados
        </el-button>
      </div>
      <div class="dish-list available-list">
        <div v-for="dish in availableDishes" :key="dish.key" class="available-dish">
          <el-checkbox :value="value.includes(dish.key)" @change="toggleDish(dish.key, $event)">
            <span class="dish-name">{{ dish.name }}</span>
            <span v-if="dish.description" class="dish-description">{{ dish.description }}</span>
          </el-checkbox>
        </div>
        <p v-if="!availableDishes.length" class="empty-state">
          {{ dishes.length ? 'No hay resultados que coincidan con la búsqueda.' : emptyAvailable }}
        </p>
      </div>
    </section>

    <section class="dish-panel selected-panel" :aria-label="selectedTitle">
      <div class="panel-heading">
        <h3>{{ selectedTitle }}</h3>
        <span class="count">{{ selectedDishes.length }}</span>
      </div>
      <p class="panel-hint">Usa las flechas o cambia el número de posición.</p>
      <div class="selected-toolbar">
        <el-button size="small" :disabled="selectedDishes.length < 2" @click="sortSelected">
          Ordenar A–Z
        </el-button>
        <span>Las nuevas selecciones se añaden al final.</span>
      </div>
      <ol class="dish-list selected-list">
        <li v-for="(dish, index) in selectedDishes" :key="dish.key" class="selected-dish">
          <el-input-number
            :value="index + 1"
            :min="1"
            :max="selectedDishes.length"
            :precision="0"
            :controls="false"
            size="small"
            :label="'Posición en carta de ' + dish.name"
            @change="moveDish(dish.key, $event)"
          />
          <div class="selected-name">
            <span class="dish-name">{{ dish.name }}</span>
            <span v-if="dish.description" class="dish-description">{{ dish.description }}</span>
          </div>
          <div class="dish-actions">
            <el-button size="mini" icon="el-icon-arrow-up" :disabled="index === 0"
              :aria-label="'Subir ' + dish.name" :title="'Subir ' + dish.name"
              @click="moveDish(dish.key, index)" />
            <el-button size="mini" icon="el-icon-arrow-down" :disabled="index === selectedDishes.length - 1"
              :aria-label="'Bajar ' + dish.name" :title="'Bajar ' + dish.name"
              @click="moveDish(dish.key, index + 2)" />
            <el-button size="mini" icon="el-icon-close"
              :aria-label="'Quitar ' + dish.name" :title="'Quitar ' + dish.name"
              @click="toggleDish(dish.key, false)" />
          </div>
        </li>
        <li v-if="!selectedDishes.length" class="empty-state">
          {{ emptySelection }}
        </li>
      </ol>
    </section>
  </div>
</template>

<script>
import { sortByName, matchesSearch } from "../utils/menuDishes";

export default {
  props: {
    dishes: { type: Array, default: () => [] },
    value: { type: Array, default: () => [] },
    availableTitle: { type: String, default: "Platos disponibles" },
    selectedTitle: { type: String, default: "Orden en carta" },
    itemSingular: { type: String, default: "plato" },
    itemPlural: { type: String, default: "platos" },
    searchPlaceholder: { type: String, default: "Buscar por nombre o descripción" },
    emptyAvailable: { type: String, default: "Esta familia todavía no tiene platos." },
    emptySelection: { type: String, default: "Selecciona platos de la lista de disponibles para incluirlos en el menú." }
  },
  data() { return { search: "" }; },
  computed: {
    searchId() { return "menu-selection-search-" + this._uid; },
    availableDishes() {
      return sortByName(this.dishes).filter(dish => matchesSearch(dish, this.search));
    },
    selectedDishes() {
      return this.value.map(key => this.dishes.find(dish => dish.key === key)).filter(Boolean);
    }
  },
  methods: {
    toggleDish(key, selected) {
      if (selected && this.value.includes(key)) return;
      const next = this.value.filter(item => item !== key);
      if (selected) next.push(key);
      this.$emit("input", next);
    },
    selectVisible() {
      const next = this.value.slice();
      this.availableDishes.forEach(dish => {
        if (!next.includes(dish.key)) next.push(dish.key);
      });
      this.$emit("input", next);
    },
    moveDish(key, position) {
      if (!Number.isInteger(position)) return;
      const next = this.value.slice();
      const from = next.indexOf(key);
      if (from < 0) return;
      const to = Math.max(0, Math.min(next.length - 1, position - 1));
      next.splice(from, 1);
      next.splice(to, 0, key);
      this.$emit("input", next);
    },
    sortSelected() {
      this.$emit("input", sortByName(this.selectedDishes).map(dish => dish.key));
    }
  }
};
</script>

<style scoped>
.dish-selector { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; text-align: left; }
.visually-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
.dish-panel { min-width: 0; border: 1px solid #dce2e5; border-radius: 12px; padding: 18px; background: #fff; }
.selected-panel { background: #f7f9fa; }
.panel-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.panel-heading h3 { margin: 0; color: #37474f; font-size: 18px; }
.count { border-radius: 16px; background: #e6edef; color: #37474f; padding: 3px 10px; font-weight: 600; }
.panel-hint { color: #606b72; font-size: 13px; margin: 10px 0 16px; }
.search-summary, .selected-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; min-height: 48px; font-size: 12px; color: #606b72; }
.selected-toolbar { margin-bottom: 12px; flex-wrap: wrap; }
.dish-list { max-height: 48vh; overflow-y: auto; margin: 0; padding: 0; list-style: none; }
.available-dish { padding: 12px 4px; border-bottom: 1px solid #edf0f2; }
.available-dish ::v-deep .el-checkbox { display: flex; align-items: flex-start; width: 100%; }
.available-dish ::v-deep .el-checkbox__input { margin-top: 3px; }
.available-dish ::v-deep .el-checkbox__label { min-width: 0; white-space: normal; line-height: 1.5; }
.dish-name { display: block; font-size: 14px; color: #273941; overflow-wrap: anywhere; }
.dish-description { display: block; font-size: 12px; line-height: 1.5; color: #606b72; margin-top: 3px; overflow-wrap: anywhere; }
.selected-dish { display: flex; align-items: center; gap: 10px; padding: 12px 0; border-bottom: 1px solid #dce2e5; }
.selected-dish .el-input-number { width: 52px; flex: 0 0 52px; }
.selected-name { flex: 1; min-width: 0; }
.dish-actions { display: flex; gap: 4px; }
.dish-actions .el-button { padding: 7px; margin: 0; }
.empty-state { color: #606b72; padding: 24px 12px; text-align: center; font-size: 14px; line-height: 1.6; }
@media (max-width: 760px) {
  .dish-selector { grid-template-columns: 1fr; }
  .dish-list { max-height: 32vh; }
  .dish-panel { padding: 12px; }
}
</style>
